// Document Center — mock data (DOCUMENT_CENTER_HANDOFF §8, Oct 15 2026). UI only.
import type { Card, Check, Doc } from './types';
import { PMS } from '@/features/shared/pms';

export const DC_CARDS_TO_CHARGE = 3;
export const DC_CARDS_TO_CHARGE_TOTAL = 827.0;
export const DC_CHECKS_COUNT = 14;
export const DC_CHECKS_NOT_IN_BANK = 3;
export const DC_FILED_THIS_WEEK = 4;
export const DC_YESTERDAY_COUNT = 9;

function heldDoc(h: {
  id: string;
  kind: Doc['kind'];
  via: 'email' | 'fax';
  sender: string;
  arrived: string;
  pages: number;
  files?: string[];
  looksLike: string;
  deadline?: { label: string; iso: string };
  failedChecks?: boolean;
}): Doc {
  const { id, kind, via, sender, arrived, pages, looksLike } = h;
  const pageWord = `${String(pages)} page${pages === 1 ? '' : 's'}`;
  return {
    id,
    kind,
    kindLabel: 'From an unknown sender',
    group: 'needs',
    status: 'needs',
    source: `${via === 'fax' ? 'Fax' : 'Email'} · ${arrived}`,
    metaLine: `${via === 'fax' ? 'Fax from' : 'Email from'} ${sender} · ${arrived} · ${pageWord}`,
    title: `${via === 'fax' ? 'Fax' : 'Email'} from ${sender}`,
    wentToLabel: 'Held, nothing read yet',
    pages: [],
    read: [],
    doneTone: 'progress',
    doneTitle: '',
    doneSteps: [],
    doneLinks: [],
    history: `History: ${via === 'fax' ? 'received by fax' : 'received by email'} ${arrived} · held, ELVA has not read it`,
    held: {
      via,
      sender,
      arrived,
      pages,
      looksLike,
      ...(h.files ? { files: h.files } : {}),
      ...(h.deadline ? { deadline: h.deadline } : {}),
      ...(h.failedChecks ? { failedChecks: true } : {}),
    },
  };
}

export function buildDocuments(): Doc[] {
  return [
    // ---- Needs you ----
    {
      id: 'DOC-BB',
      kind: 'eob',
      kindLabel: 'EOB',
      group: 'needs',
      status: 'needs',
      source: 'Fax · today 10:20 AM',
      metaLine: 'Fax · today 10:20 AM · 2 pages · check 30071 · $412.80',
      title: 'Bluebonnet Dental Plan: 2 amounts are unreadable',
      wentToLabel: 'A person needs to read it',
      pages: [
        {
          alt: 'Page 1 of the fax. Two paid amounts are smudged.',
          caption: 'Page 1 · the two smudged amounts are marked',
          text: `PATIENT       DOS       PROC   PAID
ROSS, ADAM    09/29/26  D1110  ⟪smudged⟫
ROSS, ADAM    09/29/26  D0274  ⟪smudged⟫
WEBB, NORA    10/01/26  D2391    268.00
                       CHECK TOTAL 412.80`,
        },
      ],
      read: [
        { label: 'Payer', value: 'Bluebonnet Dental Plan' },
        { label: 'Check', value: '30071 · $412.80' },
        { label: 'Patients', value: 'Adam Ross (2 lines), Nora Webb (1 line)' },
        { label: 'Unreadable', value: 'Adam Ross · D1110 and D0274, the paid amounts', note: 'smudged; the check total gives the pair but not each one' },
      ],
      doneTone: 'progress',
      doneTitle: '',
      doneSteps: [],
      doneLinks: [],
      history: 'History: received by fax 10:20 AM · read by ELVA 10:21 AM · waiting for a person',
      tries: [
        { time: '10:21 AM', head: 'Check total.', body: 'Two amounts are smudged, so the total can’t tell which is which.', logId: 'dc-bb-1' },
        { time: '10:22 AM', head: 'Fee schedule.', body: 'No fee schedule on file for Bluebonnet.', logId: 'dc-bb-2' },
        { time: '10:24 AM', head: 'Portal.', body: 'Bluebonnet has no provider portal.', logId: 'dc-bb-3' },
        { time: '10:31 AM', head: 'Call to Bluebonnet.', body: 'They’ll mail a clean copy in 7–10 days. Reference BB-55102.', logId: 'dc-bb-4' },
      ],
      fields: [
        { id: 'd1110', label: 'Adam Ross · D1110 · paid' },
        { id: 'd0274', label: 'Adam Ross · D0274 · paid' },
      ],
      constraint: { total: 144.8, text: 'They must add up to $144.80.' },
      waitForClean: true,
      hitlItemId: 'HL-BLUEBONNET',
    },


    // ---- Needs you · held, from an unknown sender (D180, D184, D250). Nothing is read yet ----
    heldDoc({
      id: 'DOC-HELD-DENIAL',
      kind: 'denial',
      via: 'fax',
      sender: '(214) 555-0147',
      arrived: 'today 9:42 AM',
      pages: 3,
      looksLike: 'Looks like a denial letter · appeal by Nov 30',
      deadline: { label: 'Nov 30', iso: '2026-11-30' },
    }),
    heldDoc({
      id: 'DOC-HELD-EOB',
      kind: 'eob',
      via: 'email',
      sender: 'remits@sunlife-dental-pay.com',
      arrived: 'yesterday 4:15 PM',
      pages: 4,
      files: ['SunLife_remit_1021.pdf', 'SunLife_remit_1021_p2.pdf'],
      looksLike: 'Looks like an EOB with a check',
    }),
    heldDoc({
      id: 'DOC-HELD-FAILED',
      kind: 'other',
      via: 'email',
      sender: 'billing@cigna-dental-payments.com',
      arrived: 'today 7:58 AM',
      pages: 1,
      files: ['Cigna_update_billing_details.pdf'],
      looksLike: 'Looks like a change of payment details',
      failedChecks: true,
    }),

    // ---- Today ----
    {
      id: 'DOC-GUARDIAN-REQ',
      kind: 'request',
      kindLabel: 'Request letter',
      group: 'today',
      status: 'done',
      source: 'Fax · 11:05 AM',
      metaLine: 'Fax from Guardian · today 11:05 AM · 1 page',
      title: 'Guardian asks for the perio chart for Kara Singh',
      wentToLabel: 'ELVA sent it · Insurance A/R',
      pages: [
        {
          alt: 'The faxed letter from Guardian, page 1',
          caption: 'Page 1 of 1',
          text: `GUARDIAN DENTAL CLAIMS
REQUEST FOR ADDITIONAL INFORMATION

Patient:   KARA SINGH
Claim #:   GD-2026-1184470
DOS:       10/09/2026
Procedure: D4341 x2

Please submit a periodontal chart
within 30 days of this letter.
Date of letter: 10/13/2026`,
        },
      ],
      read: [
        { label: 'Payer', value: 'Guardian' },
        { label: 'Patient', value: 'Kara Singh' },
        { label: 'Claim', value: 'Visit of Oct 9 · $1,015.00' },
        { label: 'Asks for', value: 'A periodontal chart' },
        { label: 'Due by', value: 'Nov 12, 2026' },
      ],
      doneTone: 'done',
      doneTitle: 'ELVA sent Guardian the perio chart',
      doneSteps: [
        { time: '11:07 AM', text: 'matched to Kara Singh’s claim of Oct 9 in Insurance A/R.' },
        { time: '11:08 AM', text: `found her perio chart of Sep 28 in ${PMS}.` },
        { time: '11:12 AM', text: 'uploaded it to the claim on Guardian’s portal. Guardian confirmed receipt.' },
        { time: '11:13 AM', text: `filed this letter to Kara’s documents in ${PMS}.` },
      ],
      doneLinks: [
        { label: 'Kara’s claim in Insurance A/R', href: '/ar/AR-KARA' },
      ],
      history: 'History: received by fax 11:05 AM · read by ELVA 11:06 AM · nothing for anyone to do',
    },
    {
      id: 'DOC-CIGNA-PA',
      kind: 'preauth',
      kindLabel: 'Pre-auth answer',
      group: 'today',
      status: 'done',
      source: 'Portal · 10:48 AM',
      metaLine: 'From Cigna’s portal · today 10:48 AM',
      title: 'Cigna approved Daniel Foster’s implant',
      wentToLabel: 'Pre-auths › Approved',
      pages: [
        {
          alt: 'The pre-authorization answer downloaded from Cigna’s portal',
          caption: 'Downloaded from Cigna’s portal',
          text: `CIGNA DENTAL — PRE-AUTHORIZATION
Member:    DANIEL FOSTER
Procedure: D6010 implant, #19
Decision:  APPROVED
Reference: PA-88213
Valid to:  04/15/2027`,
        },
      ],
      read: [
        { label: 'Payer', value: 'Cigna' },
        { label: 'Patient', value: 'Daniel Foster' },
        { label: 'Procedure', value: 'Implant #19 (D6010)' },
        { label: 'Decision', value: 'Approved · ref PA-88213' },
        { label: 'Valid until', value: 'Apr 15, 2027' },
      ],
      doneTone: 'done',
      doneTitle: 'ELVA updated the pre-auth',
      doneSteps: [
        { time: '10:49 AM', text: 'matched to Daniel Foster’s pre-auth.' },
        { time: '10:49 AM', text: 'marked it Approved in Pre-auths, ref PA-88213, and wrote “Valid until Apr 15, 2027” to the pre-auth.' },
        { time: '10:50 AM', text: `filed the answer to Daniel’s documents in ${PMS}.` },
      ],
      doneLinks: [{ label: 'See Daniel’s pre-auth', href: '/pre-auths/PA-T3' }],
      history: 'History: downloaded from the portal 10:48 AM · read by ELVA 10:48 AM',
    },
    {
      id: 'DOC-DELTA-CHECK',
      kind: 'eob_check',
      kindLabel: 'EOB + check',
      group: 'today',
      status: 'done',
      source: 'Fax · 9:12 AM',
      metaLine: 'Fax from Delta Dental of Texas · today 9:12 AM · 4 pages',
      title: 'Delta Dental of Texas · check 004417820 · $3,486.20',
      wentToLabel: 'Posting › To post',
      pages: [
        {
          alt: 'The Delta Dental EOB with the check',
          caption: 'Page 1 of 4',
          text: `DELTA DENTAL OF TEXAS
EXPLANATION OF BENEFITS
Check: 004417820    Amount: $3,486.20
9 claims — see pages 2–4`,
        },
      ],
      read: [
        { label: 'Payer', value: 'Delta Dental of Texas' },
        { label: 'Check', value: '004417820 · $3,486.20' },
        { label: 'Claims', value: '9 claims' },
      ],
      doneTone: 'done',
      doneTitle: 'ELVA sent it to Posting',
      doneSteps: [
        { time: '9:13 AM', text: 'Sent to Posting › To post: 9 claims, $3,486.20.' },
        { time: '9:13 AM', text: `filed the EOB in ${PMS} under the check.` },
      ],
      doneLinks: [{ label: 'See the check in Document Center › Checks', href: '/document-center/checks' }],
      history: 'History: received by fax 9:12 AM · read by ELVA 9:13 AM',
    },
    {
      id: 'DOC-GUARDIAN-CHECK',
      kind: 'eob_check',
      kindLabel: 'EOB + check',
      group: 'today',
      status: 'done',
      source: 'Fax · 8:40 AM',
      metaLine: 'Fax from Guardian · today 8:40 AM · 3 pages',
      title: 'Guardian · check 77120458 · $1,284.50',
      wentToLabel: 'Posting › To post · page 3 from the portal',
      pages: [
        {
          alt: 'The Guardian EOB with the check',
          caption: 'Page 1 of 3',
          text: `GUARDIAN
EXPLANATION OF BENEFITS
Check: 77120458    Amount: $1,284.50
4 claims`,
        },
      ],
      read: [
        { label: 'Payer', value: 'Guardian' },
        { label: 'Check', value: '77120458 · $1,284.50' },
        { label: 'Claims', value: '4 claims' },
        { label: 'Note', value: 'Page 3 was missing from the fax', note: 'ELVA downloaded page 3 from Guardian’s portal' },
      ],
      doneTone: 'done',
      doneTitle: 'ELVA sent it to Posting',
      doneSteps: [
        { time: '8:41 AM', text: 'page 3 was missing from the fax; ELVA downloaded it from Guardian’s portal.' },
        { time: '8:42 AM', text: 'Sent to Posting › To post: 4 claims, $1,284.50.' },
        { time: '8:42 AM', text: `filed the EOB in ${PMS} under the check.` },
      ],
      doneLinks: [{ label: 'See the check in Document Center › Checks', href: '/document-center/checks' }],
      history: 'History: received by fax 8:40 AM · read by ELVA 8:42 AM',
    },
    {
      id: 'DOC-CIGNA-CARD',
      kind: 'virtual_card',
      kindLabel: 'Virtual card',
      group: 'today',
      status: 'done',
      source: 'Fax · 10:42 AM',
      metaLine: 'Fax from Cigna · today 10:42 AM · 1 page',
      title: 'Cigna · $96.00',
      wentToLabel: 'Virtual cards',
      // ELVA couldn't black out the card number, so it didn't keep the page (D216).
      pages: [],
      read: [
        { label: 'Payer', value: 'Cigna' },
        { label: 'Amount', value: '$96.00' },
        { label: 'Pays', value: '1 claim' },
      ],
      doneTone: 'done',
      doneTitle: 'ELVA sent it to Virtual cards',
      doneSteps: [
        { time: '10:43 AM', text: 'Read the amount, payer and claims. It couldn’t black out the card number, so it didn’t keep the page.' },
        { time: '10:43 AM', text: 'Sent to Virtual cards. It’s waiting to be charged from your fax inbox.' },
      ],
      doneLinks: [{ label: 'See this card in Virtual cards', href: '/document-center/cards/CARD-CIGNA-96' }],
      history: 'History: received by fax 10:42 AM · read by ELVA 10:43 AM',
    },
    {
      id: 'DOC-OKAFOR-CARD',
      kind: 'insurance_card',
      kindLabel: 'Insurance card',
      group: 'today',
      status: 'done',
      source: 'iPad photo · 8:15 AM',
      metaLine: 'iPad photo · today 8:15 AM · front and back',
      title: 'David Okafor · front and back',
      wentToLabel: 'Patient insurance · plan checked',
      pages: [
        {
          alt: 'A photo of David Okafor’s insurance card',
          caption: 'Front and back',
          text: `AETNA DENTAL
Member:  DAVID OKAFOR
ID:      W812340091
Group:   TX-4420
Plan:    PPO`,
        },
      ],
      read: [
        { label: 'Payer', value: 'Aetna' },
        { label: 'Patient', value: 'David Okafor' },
        { label: 'Member ID', value: 'W812340091' },
        { label: 'Plan', value: 'PPO · group TX-4420' },
      ],
      doneTone: 'done',
      doneTitle: 'ELVA saved the card and checked the plan',
      doneSteps: [
        { time: '8:16 AM', text: `saved the card to David Okafor’s insurance and filed the photo in ${PMS}.` },
        { time: '8:17 AM', text: 'checked eligibility with Stedi; the plan is active.' },
      ],
      doneLinks: [{ label: 'See David’s insurance', href: '/patients?q=David%20Okafor' }],
      history: 'History: photographed on the iPad 8:15 AM · read by ELVA 8:16 AM',
    },

    // ---- Filed, no work needed ----
    {
      id: 'DOC-REFERRAL',
      kind: 'other',
      kindLabel: 'Other',
      group: 'filed',
      status: 'filed',
      source: 'Fax · Oct 14, 2:31 PM',
      metaLine: 'Fax · Oct 14, 2:31 PM · 2 pages',
      title: 'Referral letter from Dr. Kaye for Nora Webb',
      wentToLabel: 'Filed, no work needed',
      pages: [
        {
          alt: 'A referral letter from Dr. Kaye',
          caption: 'Page 1 of 2',
          text: `DR. ELAINE KAYE, DDS
REFERRAL

Re: NORA WEBB
Referring for evaluation of #30.
Thank you.`,
        },
      ],
      read: [
        { label: 'Kind', value: 'Referral letter' },
        { label: 'Patient', value: 'Nora Webb' },
        { label: 'From', value: 'Dr. Kaye' },
      ],
      doneTone: 'done',
      doneTitle: 'Filed, no work needed',
      doneSteps: [],
      doneLinks: [],
      history: 'History: received by fax Oct 14, 2:31 PM · read by ELVA 2:32 PM',
      receivedIso: '2026-10-14',
      patient: 'Nora Webb',
      payerName: 'Dr. Kaye',
      filedTicks: [
        'Not an insurance document, so there’s no RCM work.',
        `Filed to Nora Webb’s documents in ${PMS} as “Referral letter”.`,
      ],
    },
  ];
}

export function buildCards(): Card[] {
  return [
    {
      id: 'CARD-CIGNA-318',
      payer: 'Cigna',
      amount: 318.4,
      last4: '8823',
      expires: '12/26',
      receivedDisplay: 'Received by fax Oct 12',
      expiresDisplay: 'expires Dec 11, 2026',
      source: { kind: 'fax', time: 'Oct 12, 8:31 AM' },
      status: 'to_charge',
      claims: [
        { patient: 'Mark Ellis', visit: 'visit Sep 21', amount: 210.0 },
        { patient: 'Diego Ramos', visit: 'visit Sep 30', amount: 108.4 },
      ],
      insight: { payer: 'Cigna', text: 'Cigna has paid you by card 7 times since July, costing about $86 in fees' },
    },
    {
      id: 'CARD-AMERITAS-412',
      payer: 'Ameritas',
      amount: 412.6,
      last4: '4521',
      expires: '01/27',
      receivedDisplay: 'Received by fax Oct 14',
      expiresDisplay: 'expires Jan 12, 2027',
      source: { kind: 'fax', time: 'Oct 14, 9:12 AM' },
      status: 'to_charge',
      claims: [
        { patient: 'Helen Pratt', visit: 'visit Sep 25', amount: 164.6 },
        { patient: 'Ivan Cole', visit: 'visit Oct 1', amount: 128.0 },
        { patient: 'Ruth Dawson', visit: 'visit Oct 3', amount: 120.0 },
      ],
    },
    {
      id: 'CARD-CIGNA-96',
      payer: 'Cigna',
      amount: 96.0,
      last4: '8823',
      expires: '01/27',
      receivedDisplay: 'Received by fax today',
      expiresDisplay: 'expires Jan 13, 2027',
      source: { kind: 'fax', time: 'today 10:42 AM' },
      pageKept: false,
      status: 'to_charge',
      claims: [{ patient: 'Grace Howard', visit: 'visit Oct 2', amount: 96.0 }],
      insight: { payer: 'Cigna', text: 'Cigna has paid you by card 7 times since July, costing about $86 in fees' },
    },
    // charged (6, $2,357.20) — representative
    {
      id: 'CARD-METLIFE-620',
      payer: 'MetLife',
      amount: 620.0,
      last4: '5540',
      expires: '11/26',
      receivedDisplay: 'Received Oct 10',
      expiresDisplay: 'expires Nov 30, 2026',
      source: { kind: 'portal', ref: 'payment 7710-3305' },
      status: 'charged',
      claims: [{ patient: 'Owen Fischer', visit: 'visit Sep 18', amount: 620.0 }],
      chargedBy: 'Charged Oct 13 by Ava Thompson (Lone Star Billing)',
      chargedApproval: '044921',
      chargedDate: 'Oct 13',
    },
    {
      id: 'CARD-AETNA-511',
      payer: 'Aetna',
      amount: 511.2,
      last4: '2210',
      expires: '12/26',
      receivedDisplay: 'Received Oct 9',
      expiresDisplay: 'expires Dec 8, 2026',
      source: { kind: 'fax', time: 'Oct 9, 2:15 PM' },
      status: 'charged',
      claims: [{ patient: 'Lily Tran', visit: 'visit Sep 14', amount: 511.2 }],
      chargedBy: 'Charged Oct 10 by Ava Thompson (Lone Star Billing)',
      chargedApproval: '031882',
      chargedDate: 'Oct 10',
    },
  ];
}

export function buildChecks(): Check[] {
  return [
    { id: 'CHK-DELTA', receivedDisplay: 'Today', payer: 'Delta Dental of Texas', checkNo: '004417820', amount: 3486.2, postedLabel: 'In Posting · 1 claim needs you', inBankLabel: 'Not yet', inBankTone: 'none' },
    { id: 'CHK-GUARDIAN', receivedDisplay: 'Today', payer: 'Guardian', checkNo: '77120458', amount: 1284.5, postedLabel: 'In Posting · ready', inBankLabel: 'Not yet', inBankTone: 'none' },
    { id: 'CHK-METLIFE', receivedDisplay: 'Oct 14', payer: 'MetLife', checkNo: '9031447', amount: 842.6, postedLabel: 'Posted Oct 14', inBankLabel: 'Not yet', inBankTone: 'none' },
    { id: 'CHK-CIGNA', receivedDisplay: 'Oct 14', payer: 'Cigna', checkNo: '66104822', amount: 1096.0, postedLabel: 'Posted Oct 14', inBankLabel: 'Deposited Oct 14', inBankTone: 'ok' },
    { id: 'CHK-UC', receivedDisplay: 'Oct 14', payer: 'United Concordia', checkNo: '2208815', amount: 410.0, postedLabel: 'Posted Oct 14', inBankLabel: 'Deposited Oct 14', inBankTone: 'ok' },
    { id: 'CHK-AETNA', receivedDisplay: 'Oct 12', payer: 'Aetna', checkNo: '51200987', amount: 2210.0, postedLabel: 'Posted Oct 12', inBankLabel: 'Deposited Oct 13', inBankTone: 'ok' },
  ];
}

export const DC_CHECKS_MORE = { count: 8, total: 8911.3 };

export function buildMissingChecks(): Check[] {
  return [
    { id: 'CHK-GUARDIAN-SEP', receivedDisplay: 'Sep 30', payer: 'Guardian', checkNo: '6610032', amount: 1148.0, postedLabel: 'Posted Sep 30', inBankLabel: 'In Human in the loop: do you have the check?', inBankTone: 'warn', missingHitl: true },
  ];
}

export const DC_CHECK_FIGURES = {
  received: { count: 14, total: 18240.6 },
  inBank: { count: 11, total: 12627.3 },
  notYet: { count: 3, total: 5613.3 },
  lastStatement: 'Chase ··4417, Oct 1–14',
};

export const DC_CARD_MONTH = { count: 9, total: 3184.2, feeRate: 3.0, feeAbout: 95.53 };
export const DC_CARDS_CHARGED_TOTAL = 2357.2;
